import moment from 'moment';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { UserImage } from '../../components/controls/UserImage/UserImage';
import { UIButton } from '../../components/controls/ui/UIButton/UIButton';
import { UIIcon } from '../../components/controls/ui/UIIcon/UIIcon';
import { UITooltip } from '../../components/controls/ui/UIToolTip/UIToolTip';
import { QAFeedbackModal } from '../../components/jit-qa/QAFeedbackModal';
import { QAMessageView } from '../../components/jit-qa/QAMessageView';
import { QAPromptTemplateModal } from '../../components/promptModal/QAPromptTemplateModal';
import { LoadingSplash } from '../../components/splash/LoadingSplash';
import { SidebarRoutes } from '../../containers/SidebarContainer';
import { trackEvent } from '../../extra/sharedMethods';
import { QAMessage } from '../../models/QAmodels';
import { SocialAppUsers } from '../../models/User';
import { invokeFastApi } from '../../scripts/apis/fastapi';
import { AnalyticsEvent } from '../../scripts/constants/analytics-event';
import { useToaster, useUser } from '../../scripts/hooks';
import { logDebug } from '../../scripts/utils';

interface Author {
  displayName?: string;
  email: string;
  icon?: string;
}

export const SharableLinksPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const [author, setAuthor] = React.useState<Author>();
  const [messages, setMessages] = React.useState<QAMessage[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const toaster = useToaster();
  const history = useHistory();
  const user = useUser();

  const teamMates = useMemo(() => {
    const seen = new Set();

    return user?.orgByOrg.appPreferences.recommended_apps_list?.reduce(
      (acc: SocialAppUsers[], app) => {
        if (app.social_info_list.length > 0) {
          for (const socialInfo of app.social_info_list) {
            if (acc.length > 5) continue;
            if (seen.has(socialInfo.email)) continue;
            seen.add(socialInfo.email);
            acc.push(socialInfo);
          }
        }

        return acc;
      },
      []
    );
  }, [user]);

  useEffect(() => {
    const fetchMessages = async () => {
      setLoading(true);
      try {
        const { author: responseAuthor, messages: responseMessages } =
          await invokeFastApi<{
            author: Author;
            messages: QAMessage[];
          }>({
            path: `/topics/topicById/${id}`,
            method: 'GET',
            queryParams: {
              api_version: '2',
            },
          });

        setAuthor(responseAuthor);
        setMessages(responseMessages);
      } catch (error) {
        logDebug(error);
        history.push(SidebarRoutes.JitQA);
      }

      setLoading(false);
    };

    fetchMessages();
    trackEvent(AnalyticsEvent.SharableLinkViewed);
  }, [id, history]);

  const { topicTitle, topicTimestamp } = useMemo(() => {
    if (messages[0]) {
      return {
        topicTitle: messages[0].topic_title,
        topicTimestamp: messages[0].conversation_timestamp,
      };
    }

    return {
      topicTitle: '',
      topicTimestamp: '',
    };
  }, [messages]);

  const handleCopyLink = useCallback(() => {
    navigator.clipboard.writeText(window.location.href);
    toaster.success('Link copied to clipboard');
    trackEvent(AnalyticsEvent.SharableLinkCreated);
  }, [toaster]);

  const handleStartChat = useCallback(() => {
    history.push(SidebarRoutes.JitQA);
  }, [history]);

  if (loading) {
    return <LoadingSplash />;
  }

  return (
    <div>
      <div className="sticky top-0 flex z-10 p-4 justify-end gap-4 border-b bg-white">
        <UIButton onClick={handleCopyLink} type="secondary">
          Copy Link
        </UIButton>
        <UIButton onClick={handleStartChat} type="primary">
          Start a chat
        </UIButton>
      </div>
      <div className="max-w-3xl mx-auto my-8">
        <div className="px-2 sm:px-0">
          <div className="text-2xl font-semibold">{topicTitle}</div>
          <div className="flex gap-2 mt-2">
            <UserImage imageUrl={author?.icon} size={24} />
            <div className="font-medium">
              {author?.displayName ?? author?.email}
            </div>
            <div className="text-cloud-20">|</div>
            <div className="font-light">
              {moment(topicTimestamp).format('MMM D, h:mm a')}
            </div>
          </div>
        </div>
        <div className="mt-10">
          {messages.map((message) => (
            <QAMessageView
              key={message.row_id}
              qaMessage={message}
              senderDisplayName={author?.displayName}
              senderPhotoUrl={author?.icon}
              sharableConversation={messages}
            />
          ))}
        </div>
      </div>
      <div>
        <div className=" sm:mx-16 border-b" />
        <div className="mt-7 mx-auto max-w-3xl px-2 sm:px:0">
          <div className="flex sm:flex-row flex-col gap-3 justify-center mb-8">
            <div className="bg-cloud-15 rounded p-6">
              <div className="font-medium">
                Team members using Dash AI as their Knowledge Assistant.
              </div>
              <div className="flex mt-6">
                {teamMates?.map((teamMate) => (
                  <div className="-mr-3" key={teamMate.email}>
                    <UITooltip title={teamMate.email}>
                      <div>
                        <UserImage
                          displayName={teamMate.name}
                          imageUrl={teamMate.icon}
                          size={48}
                        />
                      </div>
                    </UITooltip>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-cloud-15 rounded p-6">
              <div className="font-medium">
                One assistant for all your use cases at work.
              </div>
              <div className="grid grid-cols-2 gap-1 mt-6">
                <div className="flex gap-2 items-center p-2 bg-white rounded-2xl">
                  <UIIcon name="slack" size={18} type="apps" />
                  <div className="text-xs">Summary of this thread</div>
                </div>
                <div className="flex gap-2 items-center p-2 bg-white rounded-2xl">
                  <UIIcon name="jira" size={18} type="apps" />
                  <div className="text-xs">Who is assigned a ticket</div>
                </div>
                <div className="flex gap-2 items-center p-2 bg-white rounded-2xl">
                  <UIIcon name="notion" size={18} type="apps" />
                  <div className="text-xs">Find me the latest standup doc</div>
                </div>
                <div className="flex gap-2 items-center p-2 bg-white rounded-2xl">
                  <UIIcon name="github" size={18} type="apps" />
                  <div className="text-xs">Explain the code of this file</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <QAPromptTemplateModal />
      <QAFeedbackModal sharableConversation={messages} />
    </div>
  );
};
